import { ChatMessage, ChatThreadSummary, formatThreadTitle } from "../datastore/ChatDataStore";

// Friendly progress labels for the graph's node names. Unknown nodes fall
// back to a generic label so new graph nodes degrade gracefully.
const NODE_STATUS_LABELS: { [node: string]: string } = {
    input_adapter: "Reading your question...",
    build_prompt: "Preparing context...",
    call_llm: "Thinking...",
    validate_sql: "Validating SQL...",
    execute_query: "Running query...",
    append_exploration_result: "Exploring the data...",
    summarize: "Summarizing results...",
    render_response: "Finishing up..."
};

// Sentinel phase set the instant a send starts, before the first node update.
export const SENDING_PHASE = "sending";

// Display labels for the streamed thinking steps. Unknown steps degrade to a
// generic label so a new graph step does not render a blank prefix.
const THINKING_STEP_LABELS: { [step: string]: string } = {
    planning: "Planning",
    exploring: "Exploring",
    summarizing: "Summarizing"
};

export interface FmsChatThreadRowView {
    id: string;
    title: string;
    timestamp: string;
}

export interface ThinkingSegment {
    step: string;
    text: string;
}

// Pure display converter (see CLAUDE.md "Front-end Architecture Standards").
// No React imports and no constructor deps — chat formatting is context-free
// (the Eastern-time display rule is constant per the repo date convention).
export class FmsChatDisplayConverter {
    // The heading mirrors the active conversation's sidebar title, derived
    // from the open transcript so a brand-new conversation is titled by its
    // optimistic first question immediately.
    public conversationTitle(messages: ChatMessage[]): string {
        const firstQuestion = messages.find(m => m.role === "human");
        return firstQuestion ? formatThreadTitle(firstQuestion.content) : "New conversation";
    }

    // phase is the raw node name, the SENDING_PHASE sentinel, or null (idle).
    public nodeStatusLabel(phase: string | null): string | null {
        if (phase === null) {
            return null;
        }
        if (phase === SENDING_PHASE) {
            return "Sending...";
        }
        return NODE_STATUS_LABELS[phase] ?? "Working...";
    }

    public thinkingStepLabel(step: string): string {
        return THINKING_STEP_LABELS[step] ?? "Thinking";
    }

    // Friendly date + time for the thread list: "Today" / "Yesterday" for the
    // last two Eastern calendar days, otherwise a long date like "June 3, 2026",
    // each followed by the Eastern time, e.g. "Today, 9:00 AM". `now` is
    // injectable so the comparison is testable. Eastern per the repo's date
    // convention; yesterday is derived with UTC date math to dodge DST.
    public friendlyDate(iso: string, now: Date = new Date()): string {
        const TZ = "America/New_York";
        const target = new Date(iso);
        const time = target.toLocaleTimeString("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit" });
        const easternYmd = (d: Date): string => d.toLocaleDateString("en-CA", { timeZone: TZ });
        const targetYmd = easternYmd(target);
        const todayYmd = easternYmd(now);
        let day: string;
        if (targetYmd === todayYmd) {
            day = "Today";
        } else {
            const [y, m, d] = todayYmd.split("-").map(Number);
            const yesterday = new Date(Date.UTC(y, m - 1, d) - 86_400_000);
            const pad = (n: number): string => String(n).padStart(2, "0");
            const yesterdayYmd = `${yesterday.getUTCFullYear()}-${pad(yesterday.getUTCMonth() + 1)}-${pad(yesterday.getUTCDate())}`;
            day = targetYmd === yesterdayYmd
                ? "Yesterday"
                : target.toLocaleDateString("en-US", { timeZone: TZ, month: "long", day: "numeric", year: "numeric" });
        }
        return `${day}, ${time}`;
    }

    public threadRow(thread: ChatThreadSummary, now: Date = new Date()): FmsChatThreadRowView {
        return {
            id: thread.threadId,
            title: thread.title ?? "Untitled",
            timestamp: this.friendlyDate(thread.createdAt, now)
        };
    }
}

export const fmsChatDisplayConverter = new FmsChatDisplayConverter();
